import React, {useState, useEffect} from "react"
import { CircularProgressbar } from "react-circular-progressbar"
import "react-circular-progressbar/dist/styles.css"
import _ from "lodash"
import { local } from 'data/lib/local-json'

import propertyService, {Property} from "data/property"
import folderService from "data/folder"
import calcService from "data/calc"
import validationService from "data/validate"
import notificationService from "data/notification"
import api_match from "data/interface/api"
import { getPropertyDetails } from "data/lib/property"
import loadingService from 'data/loading'

import { WithTip } from 'components/tooltip/with-tip'
import CalcModal, { ICalcModal } from "components/folder/calc/calc-modal"
import { CalcButton_s, FolderCalcState, FolderCalcStateIcon, FolderCalcIconWrapper, StyledFolderCalcIcon, FolderCalcProgress } from "styles/calc-styles"
import { FolderCalcIcon } from "images/icons/header"

type ICalcButton = {
  headless?: boolean
  folderId?: number|null
  propertySubset?: object[]|null
  calcStartedCallback?: Function
}

const CalcButton = (props: ICalcButton) => {
  const [properties, setProperties] = useState<object[]>([])
  let folderid = _.last(local('folderPath'))

  const [modalProps, setModalProps] = useState<ICalcModal>({
    calcConfirmVisible: false,
    propsWithErrors: [],
    propsWithSevereErrors: [],
    propsRequiringMigration: [],
    onConfirmCalc: () => {},
    onCancelCalc: () => {},
    toggleModalVisible: () => {}
  })

  useEffect(() => {
    const propertySubscription = propertyService.stream().subscribe(data => {
      api_match(
        data,
        init  => setProperties(init.value),
        ok    => setProperties(ok.value),
        err   => setProperties(err.value), // backout any optimistic updates
        fetch => {}
      )
    })

    return function cleanup() {
      propertySubscription.unsubscribe()
    }
  }, [])

  useEffect(() => {
    if(props.folderId) {
      folderid = props.folderId
      verifyFolderCalc()
    }
  }, [props.folderId])

  useEffect(() => {
    if(props.propertySubset) {
      verifyFolderCalc()
    }
  }, [props.propertySubset])

  const startCalc = () => {
    let propertyIds: Array<number> = [0]
    if(props.propertySubset) propertyIds = _.map(props.propertySubset, prop => prop.propertyid)
    else propertyIds = _.map(_.filter(properties, prop => !prop.calcdetail), prop => prop.propertyid)
    calcService.start("folder", propertyIds)
  }

  const onCancelCalc = () => setModalProps(_.set(modalProps, "calcConfirmVisible", false))

  const onConfirmCalc = () => {
    setModalProps(_.set(modalProps, "calcConfirmVisible", false))
    startCalc()
    if(props.calcStartedCallback) props.calcStartedCallback()
  }

  const verifyFolderCalc = async () => {
    loadingService.toggleLoading(true, 'verifyFolderCalc');
    if(props.propertySubset) folderid = _.get(_.first(props.propertySubset), "folderid")
    await propertyService.fetchByFolderId(folderid, false)
    const _properties = props.propertySubset || propertyService.value()

    let calculatable: Array<{id: number, code: string}> = [];
    let propsWithErrors: Array<{}> = [];
    let propsWithSevereErrors: Array<{}> = [];
    _properties
      .filter(prop => !_.get(prop, "calcdetail"))
      .forEach(prop => {
        let {errordetail, severe} = getPropertyDetails(prop);
        calculatable.push({id:_.get(prop, "propertyid"), code: _.get(prop, "code")});
        if(errordetail) propsWithErrors.push(prop);
        if(severe) propsWithSevereErrors.push(prop);
      });

    const propsRequiringMigration = []

    if(!calculatable.length) {
      notificationService.notify({
        type: "info",
        message: "All properties in the current folder are calculated or are currently being calculated",
        dismiss: 3000
      })
    }
    else if(propsWithErrors.length || propsWithSevereErrors.length || propsRequiringMigration.length) {
      setModalProps({calcConfirmVisible: true, propsWithErrors, propsWithSevereErrors, propsRequiringMigration, onConfirmCalc, onCancelCalc, toggleModalVisible: () => {}})
    } else {
      setModalProps({calcConfirmVisible: false, propsWithErrors, propsWithSevereErrors, propsRequiringMigration, onConfirmCalc, onCancelCalc, toggleModalVisible: () => {}})
      startCalc()
      if(props.calcStartedCallback) props.calcStartedCallback()
    }
    loadingService.toggleLoading(false, 'verifyFolderCalc');
  }

  let validationComponent, hasErrors
  {
    const showValidationErrors = folder => {
      const folderid = folder?.folderid
      function validationFilter(errors) {
        if (!_.keys(_.get(errors, 'errordetail.folder')).length) return errors
        const folder = _.chain(errors).get('errordetail.folder').pick(folderid).value()
        if (!folder) return errors
        const errordetail = {folder}
        return {
          ...errors,
          errordetail,
          calcDetail: undefined
        }
      }

      validationService.displayValidationErrors(undefined, validationFilter)
    }

    const folder = folderService.value().find(f => _.get(f, "folderid") === folderid)
    hasErrors = !_.isEmpty(_.get(validationService.value(), ['errordetail', 'folder', folderid]))
    validationComponent =  (window.location.pathname.includes('folder') && hasErrors &&
      <WithTip position="bottom">
        <span>Calculate all properties in the current folder</span>
        <FolderCalcState notCalcError={true} id="folderCalcStatusContainer" onClick={() => showValidationErrors(folder)}>
          <div>
            <FolderCalcStateIcon notCalcError={true}>
              Warning: Click to Review Inputs
            </FolderCalcStateIcon>
          </div>
        </FolderCalcState>
      </WithTip>
    );
  }

  let calcProgressComponent
  if(window.location.pathname.includes('folder') &&
     propertyService.value().some(p => _.get(p, 'calcdetail.status') === 'Pending')) {
    const properties = _.filter(propertyService.value(), {folderid})
    const total = properties.length
    const complete = total - properties.filter(p => _.get(p, 'calcdetail.status') === 'Pending').length
    if (complete != total) {
      calcProgressComponent = (
        <WithTip position="bottom">
          <span>{`${complete} of ${total} complete`}</span>
          <FolderCalcProgress id="folderCalcStatusContainer">
            <CircularProgressbar value={100 * complete/total} styles={{root:{width: '14px'}}} />
            <span>{`${complete} of ${total} calculated`}</span>
          </FolderCalcProgress>
        </WithTip>
      )
    }
  }

  if(props.headless) {
    return <CalcModal {...modalProps} />
  } else {
    return (
      <CalcButton_s>
        <CalcModal {...modalProps} />
        {window.location.pathname.includes('folder') &&
          <WithTip position="bottom">
            <span>Calculate all properties in the current folder</span>
            <a onClick={() => verifyFolderCalc()} id="folderCalcStatusContainer">
              { hasErrors ?
                <StyledFolderCalcIcon notCalcError={true} src={FolderCalcIcon} />
                :
                <FolderCalcIconWrapper src={FolderCalcIcon}/>
              }
            </a>
          </WithTip>
        }
        {validationComponent}
        {calcProgressComponent}
      </CalcButton_s>
    )
  }
}

export default CalcButton
